// IP validation utilities
export function parseIPRanges(
  ipRangeText: string | null | undefined
): string[] {
  if (!ipRangeText || typeof ipRangeText !== "string") return [];

  return ipRangeText
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export function isIPInRange(
  clientIP: string,
  allowedRanges: string[]
): boolean {
  if (!clientIP || allowedRanges.length === 0) return true;

  for (const range of allowedRanges) {
    if (isIPInSingleRange(clientIP, range)) {
      return true;
    }
  }
  return false;
}

function isIPInSingleRange(clientIP: string, range: string): boolean {
  // Handle single IP
  if (!range.includes("/") && !range.includes("-")) {
    return clientIP === range;
  }

  // Handle CIDR notation (e.g., 192.168.1.0/24)
  if (range.includes("/")) {
    return isIPInCIDR(clientIP, range);
  }

  // Handle IP range (e.g., 192.168.1.1-192.168.1.10)
  if (range.includes("-")) {
    const [start, end] = range.split("-").map((ip) => ip.trim());
    return isIPInIPRange(clientIP, start, end);
  }

  return false;
}

function isIPInCIDR(ip: string, cidr: string): boolean {
  const [network, prefixLength] = cidr.split("/");
  const prefix = parseInt(prefixLength, 10);

  const ipParts = ip.split(".").map(Number);
  const networkParts = network.split(".").map(Number);

  const ipBinary =
    (ipParts[0] << 24) + (ipParts[1] << 16) + (ipParts[2] << 8) + ipParts[3];
  const networkBinary =
    (networkParts[0] << 24) +
    (networkParts[1] << 16) +
    (networkParts[2] << 8) +
    networkParts[3];

  const mask = (-1 << (32 - prefix)) >>> 0;

  return (ipBinary & mask) === (networkBinary & mask);
}

function isIPInIPRange(ip: string, startIP: string, endIP: string): boolean {
  const ipParts = ip.split(".").map(Number);
  const startParts = startIP.split(".").map(Number);
  const endParts = endIP.split(".").map(Number);

  const ipNumber =
    (ipParts[0] << 24) + (ipParts[1] << 16) + (ipParts[2] << 8) + ipParts[3];
  const startNumber =
    (startParts[0] << 24) +
    (startParts[1] << 16) +
    (startParts[2] << 8) +
    startParts[3];
  const endNumber =
    (endParts[0] << 24) +
    (endParts[1] << 16) +
    (endParts[2] << 8) +
    endParts[3];

  return ipNumber >= startNumber && ipNumber <= endNumber;
}

import {
  getServerTimeIST,
  formatISTDate,
  formatISTTime,
} from "./timezoneUtils";

// Time validation utilities
export function isAssessmentTimeValid(
  collegeVisitDate: string | null,
  startTime: string | null,
  durationMinutes: number | null,
  serverTimeOffset: number
): { isValid: boolean; message: string; timeRemaining?: number } {
  if (!collegeVisitDate || !startTime || !durationMinutes) {
    return {
      isValid: false,
      message: "Assessment schedule not configured for this college.",
    };
  }

  // Use server time instead of client time for all calculations
  const serverTime = getServerTimeIST(serverTimeOffset);
  const currentDate = formatISTDate(serverTime);
  const currentTime = formatISTTime(serverTime);

  // Check if current date matches the college visit date
  if (currentDate !== collegeVisitDate) {
    return {
      isValid: false,
      message: `Assessment is only available on ${new Date(
        collegeVisitDate
      ).toLocaleDateString("en-GB")}.`,
    };
  }

  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [currentHours, currentMinutesFromTime, currentSeconds = 0] = currentTime
    .split(":")
    .map(Number);

  // Calculate time in total minutes from midnight for easier comparison
  const assessmentStartTotalMinutes = startHours * 60 + startMinutes;
  const currentTotalMinutes = currentHours * 60 + currentMinutesFromTime;
  const assessmentEndTotalMinutes =
    assessmentStartTotalMinutes + durationMinutes;

  if (currentTotalMinutes < assessmentStartTotalMinutes) {
    const timeUntilStart = assessmentStartTotalMinutes - currentTotalMinutes;
    return {
      isValid: false,
      message: `Assessment will start in ${timeUntilStart} minutes at ${startTime} IST.`,
    };
  }

  if (currentTotalMinutes > assessmentEndTotalMinutes) {
    const endHours = Math.floor(assessmentEndTotalMinutes / 60);
    const endMinutes = assessmentEndTotalMinutes % 60;
    const endTime = `${endHours.toString().padStart(2, "0")}:${endMinutes
      .toString()
      .padStart(2, "0")}`;
    return {
      isValid: false,
      message: `Assessment time has expired. Assessment ended at ${endTime} IST.`,
    };
  }

  // Calculate time remaining in seconds
  const timeRemainingMinutes = assessmentEndTotalMinutes - currentTotalMinutes;
  const timeRemainingSeconds = timeRemainingMinutes * 60 - currentSeconds;

  return {
    isValid: true,
    message: "Assessment is active (based on server time).",
    timeRemaining: Math.max(0, timeRemainingSeconds),
  };
}

export async function getClientIP(): Promise<string> {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error("Failed to get client IP:", error);
    return "";
  }
}
